'use client'

import { useCallback } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import type { SearchFilterState } from '@/components/portal/search-filter-bar'

/**
 * Keeps a list/directory's search + filters + page in the URL query string so
 * the view is deep-linkable and shareable (the "URL is source of truth" rule).
 * `q` = search text, `page` = 1-based page, each filter id = comma-separated values.
 *
 * `filterIds` should be a stable reference (define it at module scope).
 * The consuming component must be wrapped in a <Suspense> boundary (useSearchParams).
 */
export function useListQueryState(filterIds: string[]) {
  const router = useRouter()
  const pathname = usePathname()
  const params = useSearchParams()

  const search = params.get('q') ?? ''
  const page = Math.max(1, Number(params.get('page')) || 1)

  const filters: Record<string, string[]> = {}
  for (const id of filterIds) {
    const raw = params.get(id)
    if (raw) filters[id] = raw.split(',').filter(Boolean)
  }

  const state: SearchFilterState = { search, filters }

  const write = useCallback(
    (next: { search?: string; filters?: Record<string, string[]>; page?: number }) => {
      const sp = new URLSearchParams(params.toString())
      if (next.search !== undefined) {
        if (next.search) sp.set('q', next.search)
        else sp.delete('q')
      }
      if (next.filters !== undefined) {
        for (const id of filterIds) {
          const vals = next.filters[id]
          if (vals && vals.length) sp.set(id, vals.join(','))
          else sp.delete(id)
        }
      }
      if (next.page !== undefined) {
        if (next.page > 1) sp.set('page', String(next.page))
        else sp.delete('page')
      }
      const qs = sp.toString()
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
    },
    [params, pathname, router, filterIds],
  )

  // Changing search/filters resets to page 1.
  const setFilterState = useCallback(
    (s: SearchFilterState) => write({ search: s.search, filters: s.filters, page: 1 }),
    [write],
  )

  const setPage = useCallback((p: number) => write({ page: p }), [write])

  return { state, page, setFilterState, setPage }
}
